import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Check } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { fmtPrice } from "../lib/format";
import api, { resolveImg } from "../lib/api";
import { logger } from "../lib/logger";

export default function OrderConfirmation() {
  const { orderNumber } = useParams();
  const { t } = useLang();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    api.get(`/orders/number/${orderNumber}`).then((r) => setOrder(r.data)).catch((e) => { logger.error("order load failed", e); setOrder(false); });
  }, [orderNumber]);

  if (order === false) return <div className="py-40 text-center text-[#8A857D]">Order not found.</div>;
  if (!order) return <div className="py-40 text-center text-[#8A857D]">…</div>;

  const c = order.customer || {};

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-10 py-16 md:py-24">
      <div className="text-center mb-12">
        <div className="inline-flex items-center justify-center w-16 h-16 border border-[#C49A6C] mb-8">
          <Check size={28} className="text-[#C49A6C]" strokeWidth={1.4} />
        </div>
        <h1 className="serif text-4xl md:text-5xl">{t("order_received")}</h1>
        <p className="text-[#8A857D] mt-4">{t("thank_you")}</p>
      </div>

      <div className="border border-[#262421] divide-y divide-[#262421]">
        <div className="flex justify-between p-6">
          <span className="text-xs uppercase tracking-[0.2em] text-[#8A857D]">{t("order_number")}</span>
          <span className="serif text-lg text-[#C49A6C]" data-testid="confirm-order-number">{order.order_number}</span>
        </div>
        <div className="flex justify-between p-6">
          <span className="text-xs uppercase tracking-[0.2em] text-[#8A857D]">{t("order_status")}</span>
          <span className="text-sm border border-[#3a3a2a] text-[#c9b978] px-3 py-1 uppercase tracking-widest text-[11px]">{t(`status_${order.status}`)}</span>
        </div>

        <div className="p-6">
          <span className="text-xs uppercase tracking-[0.2em] text-[#8A857D]">{t("products")}</span>
          <div className="mt-5 space-y-4">
            {order.items.map((i, idx) => (
              <div key={i.product_id || i.slug} className="flex gap-4 items-center">
                <div className="w-14 h-16 bg-[#141311] overflow-hidden flex-shrink-0">{i.image && <img src={resolveImg(i.image)} alt="" className="w-full h-full object-cover" />}</div>
                <div className="flex-1">
                  <p className="serif text-lg">{i.name}</p>
                  <p className="text-xs text-[#8A857D]">{i.size} · × {i.qty}</p>
                </div>
                <span className="text-sm text-[#C49A6C]">{fmtPrice(i.price * i.qty, order.currency, t)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="p-6 space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-[#8A857D]">{t("subtotal")}</span><span>{fmtPrice(order.subtotal, order.currency, t)}</span></div>
          <div className="flex justify-between"><span className="text-[#8A857D]">{t("shipping")}</span><span>{order.shipping_cost === 0 ? "Free" : fmtPrice(order.shipping_cost, order.currency, t)}</span></div>
          {order.discount_amount > 0 && <div className="flex justify-between text-[#C49A6C]"><span>{t("discount_code")}</span><span>− {fmtPrice(order.discount_amount, order.currency, t)}</span></div>}
          <div className="flex justify-between serif text-lg pt-3 border-t border-[#262421] mt-3"><span>{t("amount")}</span><span className="text-[#C49A6C]">{fmtPrice(order.total, order.currency, t)}</span></div>
        </div>

        <div className="p-6">
          <span className="text-xs uppercase tracking-[0.2em] text-[#8A857D]">{t("delivery_address")}</span>
          <p className="text-sm text-[#F2EBE1] mt-3 leading-relaxed">
            {c.full_name}<br />
            {c.address}{c.building ? `, ${c.building}` : ""}{c.apartment ? `, ${c.apartment}` : ""}<br />
            {c.city ? `${c.city}, ` : ""}{c.emirate}, {c.country}<br />
            {c.phone} · {c.email}
          </p>
        </div>
      </div>

      <p className="text-xs text-[#6E685F] text-center mt-8 leading-relaxed">
        {order.payment_status === "paid" ? t("payment_received_note") : t("payment_note")}
      </p>
      <div className="text-center mt-8">
        <Link to="/shop" className="text-xs uppercase tracking-[0.2em] text-[#F2EBE1] link-underline">{t("continue_shopping")} →</Link>
      </div>
    </div>
  );
}
